<template>
  <div>
    <label v-if="label" class="block text-xs text-gray-500">
      {{ label }}<span v-if="required"> *</span>
    </label>
    <input
      :id="uniqueId"
      type="text"
      :value="modelValue"
      :placeholder="placeholder"
      data-1p-ignore
      @input="$emit('update:modelValue', $event.target.value)"
      class="block w-full rounded-md border-0 px-3 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset sm:text-sm"
      :class="[label ? 'mt-1' : '', error ? 'ring-red-300 focus:ring-2 focus:ring-red-500' : 'ring-gray-300 focus:ring-2 focus:ring-indigo-600']"
    />
  </div>
</template>

<script setup>
import { Loader } from "@googlemaps/js-api-loader";
import { onMounted, ref } from "vue";

defineProps({
  modelValue: { type: String, default: "" },
  label: { type: String, default: null },
  placeholder: { type: String, default: "" },
  required: { type: Boolean, default: false },
  error: { type: Boolean, default: false },
});

const emit = defineEmits(["update:modelValue", "address-selected"]);

const uniqueId = ref(`addr-${Math.random().toString(36).slice(2, 11)}`);

const parseAddressComponents = (components) => {
  const get = (type) => components.find((c) => c.types.includes(type))?.long_name || "";
  const route = get("route");
  const streetNumber = get("street_number");
  const street = streetNumber ? `${route} ${streetNumber}` : route;
  return {
    street,
    city: get("locality") || get("sublocality") || get("administrative_area_level_1"),
    zipcode: get("postal_code"),
  };
};

onMounted(async () => {
  const loader = new Loader({
    apiKey: import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY,
    version: "weekly",
  });
  const Places = await loader.importLibrary("places");

  const input = document.getElementById(uniqueId.value);
  const autocomplete = new Places.Autocomplete(input, {
    fields: ["address_components"],
    types: ["address"],
  });

  autocomplete.addListener("place_changed", () => {
    const place = autocomplete.getPlace();
    const components = place?.address_components || [];
    const parsed = parseAddressComponents(components);
    emit("update:modelValue", parsed.street);
    emit("address-selected", parsed);
  });
});
</script>
